// Month grid: the month's weeks (4–6), continuous multi-day bars. Week rows share the height and grow
// to fit every task (the grid scrolls) rather than hiding tasks behind "+N more".

import { useMemo } from 'react';
import clsx from 'clsx';
import { addDays, format, isSameMonth } from 'date-fns';
import { useDroppable } from '@dnd-kit/core';
import { Plus } from '@/components/ui/icons';
import type { Task } from '@shared/types';
import { useCalDnd, type CalDropData } from './CalendarDnd';
import { QuickAdd, TaskChip } from './TaskChip';
import { LANE_H, dayOf, layoutSegments, monthGridStart, monthWeekCount, type AddTarget } from './calendarUtils';

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const TOP_PAD = 6;
const BOTTOM_PAD = 36; // room for the day number
const INSET = 5; // gap between a chip and its day's edges

export interface MonthGridProps {
  cursor: number;
  tasks: Task[];
  today: number;
  adding: AddTarget | null;
  onAdd: (t: AddTarget | null) => void;
  onCreate: (name: string, t: AddTarget) => void;
  onOpenDay?: (day: number) => void;
}

export function MonthGrid({ cursor, tasks, today, adding, onAdd, onCreate, onOpenDay }: MonthGridProps) {
  const start = monthGridStart(cursor);
  const nWeeks = monthWeekCount(cursor);
  const weeks = useMemo(() => Array.from({ length: nWeeks }, (_, i) => addDays(start, i * 7).getTime()), [start, nWeeks]);

  return (
    <div className="cal-month">
      <div className="cal-month-rows">
        <div className="cal-month-head">
          {DAY_NAMES.map((n, i) => (
            <div key={n} className={clsx('cal-month-dow', { weekend: i === 0 || i === 6 })}>
              {n}
            </div>
          ))}
        </div>
        {weeks.map((ws) => (
          <WeekRow
            key={ws}
            weekStart={ws}
            cursor={cursor}
            tasks={tasks}
            today={today}
            adding={adding}
            onAdd={onAdd}
            onCreate={onCreate}
            onOpenDay={onOpenDay}
          />
        ))}
      </div>
    </div>
  );
}

function WeekRow({
  weekStart,
  cursor,
  tasks,
  today,
  adding,
  onAdd,
  onCreate,
  onOpenDay,
}: {
  weekStart: number;
  cursor: number;
  tasks: Task[];
  today: number;
  adding: AddTarget | null;
  onAdd: (t: AddTarget | null) => void;
  onCreate: (name: string, t: AddTarget) => void;
  onOpenDay?: (day: number) => void;
}) {
  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i).getTime()), [weekStart]);
  const segs = useMemo(() => layoutSegments(tasks, weekStart, 7, 'm'), [tasks, weekStart]);
  const lanes = segs.reduce((n, s) => Math.max(n, s.lane + 1), 0);
  return (
    <div className="cal-week" style={lanes ? { minHeight: TOP_PAD + lanes * LANE_H + BOTTOM_PAD } : undefined}>
      <div className="cal-week-days">
        {days.map((d, i) => (
          <DayCell
            key={d}
            day={d}
            weekend={i === 0 || i === 6}
            outside={!isSameMonth(d, cursor)}
            isToday={d === today}
            adding={adding?.day === d && adding.minutes === null}
            onAdd={onAdd}
            onCreate={onCreate}
            onOpenDay={onOpenDay}
          />
        ))}
        <div className="cal-week-events">
          {segs.map((s) => (
            <TaskChip
              key={s.key}
              seg={s}
              idPrefix="m"
              style={{
                top: TOP_PAD + s.lane * LANE_H,
                left: `calc(${(s.startCol / 7) * 100}% + ${s.contLeft ? 0 : INSET}px)`,
                width: `calc(${((s.endCol - s.startCol + 1) / 7) * 100}% - ${(s.contLeft ? 0 : INSET) + (s.contRight ? 0 : INSET)}px)`,
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function DayCell({
  day,
  weekend,
  outside,
  isToday,
  adding,
  onAdd,
  onCreate,
  onOpenDay,
}: {
  day: number;
  weekend: boolean;
  outside: boolean;
  isToday: boolean;
  adding: boolean;
  onAdd: (t: AddTarget | null) => void;
  onCreate: (name: string, t: AddTarget) => void;
  onOpenDay?: (day: number) => void;
}) {
  const data: CalDropData = { kind: 'day', day };
  const { setNodeRef, isOver } = useDroppable({ id: `mday:${day}`, data });
  const { suppressClick, dayRange } = useCalDnd();
  const inRange = !!dayRange && day >= dayRange.start && day <= dayRange.end;
  return (
    <div
      ref={setNodeRef}
      className={clsx('cal-day', { weekend, outside, today: isToday, over: isOver || inRange })}
      onClick={() => {
        if (suppressClick()) return;
        onAdd({ day, minutes: null });
      }}
    >
      <button
        className="cal-day-add"
        title="Add task"
        onClick={(e) => {
          e.stopPropagation();
          onAdd({ day, minutes: null });
        }}
      >
        <Plus size={12} />
      </button>
      <button
        className="cal-day-num"
        title={format(day, 'EEEE, MMM d')}
        onClick={(e) => {
          if (!onOpenDay) return;
          e.stopPropagation();
          onOpenDay(dayOf(day));
        }}
      >
        {format(day, 'd')}
      </button>
      {adding && <QuickAdd className="in-cell" onSubmit={(name) => onCreate(name, { day, minutes: null })} onCancel={() => onAdd(null)} />}
    </div>
  );
}
